import styled, { createGlobalStyle, keyframes } from "styled-components";

/* ========== GLOBAL STYLES ========== */
export const GlobalStyle = createGlobalStyle`
  * { box-sizing: border-box; }
  body {
    margin: 0;
    font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    background:
      radial-gradient(1200px 1200px at 80% -10%, rgba(34,211,238,0.16), transparent),
      radial-gradient(1000px 1000px at -10% 110%, rgba(56,189,248,0.18), transparent),
      #020617;
    color: #e5e7eb;
  }
`;

/* ========== LAYOUT PRIMITIVES ========== */
export const Page = styled.div`
  min-height: 100vh;
  max-width: 1240px;
  margin: 0 auto;
  padding: 0px 16px 16px;
  @media (max-width: 768px) {
    padding: 80px 12px 12px;
  }
`;

export const Card = styled.section`
  background: radial-gradient(100% 160% at 0% 0%, rgba(56,189,248,0.16), rgba(15,23,42,0.98));
  border: 1px solid #1f2937;
  border-radius: 16px;
  padding: 18px 18px 16px;
  margin-top: 18px;
  box-shadow: 0 18px 40px rgba(0,0,0,0.35);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 22px 46px rgba(0,0,0,0.5);
  }
  @media (max-width: 768px) {
    padding: 14px;
    margin-top: 14px;
  }
`;

export const FlexRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ gap }) => gap ?? 8}px;
`;

/* ========== CONTROLS / TABLES ========== */
export const TabsRow = styled.div`
  display: flex;
  gap: 8px;
  margin: 8px 0 10px;
  flex-wrap: wrap;
`;

export const TabButton = styled.button`
  flex: 1;
  min-width: 90px;
  padding: 8px 10px;
  border-radius: 10px;
  border: 1px solid #1f2937;
  cursor: pointer;
  background: ${({ active }) => (active ? "linear-gradient(90deg,#00f7d2,#5ce9ff)" : "#020617")};
  color: ${({ active }) => (active ? "#001018" : "#e5e7eb")};
  font-weight: ${({ active }) => (active ? 700 : 500)};
  font-size: 12px;
`;

export const TableWrap = styled.div`
  overflow-x: auto;
`;

export const Table = styled.table`
  width: 100%;
  min-width: 560px;
  border-collapse: collapse;
  font-size: 13px;
  @media (max-width: 640px) {
    font-size: 12px;
  }
`;

export const Th = styled.th`
  text-align: ${({ align }) => align || "left"};
  padding: 8px;
  color: #94a3b8;
  border-bottom: 1px solid #1f2937;
`;

export const Td = styled.td`
  text-align: ${({ align }) => align || "left"};
  padding: 8px;
  border-top: 1px solid #1f2937;
`;

export const SmallTable = styled.table`
  width: 100%;
  min-width: 520px;
  border-collapse: collapse;
  font-size: 12px;
`;

export const SmallTh = styled.th`
  text-align: ${({ align }) => align || "left"};
  padding: 6px;
  color: #94a3b8;
  border-bottom: 1px solid #1f2937;
`;

export const SmallTd = styled.td`
  text-align: ${({ align }) => align || "left"};
  padding: 6px;
  border-top: 1px solid #1f2937;
`;

export const BadgeDir = styled.span`
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  background: ${({ direction }) =>
    direction === "in" ? "rgba(34,197,94,0.18)" : "rgba(248,113,113,0.18)"};
  color: ${({ direction }) => (direction === "in" ? "#4ade80" : "#fb7185")};
`;

export const Field = styled.div`
  flex: 1;
  min-width: 220px;
  label {
    font-size: 13px;
  }
  input,
  select {
    width: 100%;
    padding: 10px;
    border-radius: 8px;
    border: 1px solid #1f2937;
    background: #020617;
    color: #e5e7eb;
    margin-top: 4px;
    font-size: 13px;
  }
`;

export const PrimaryButton = styled.button`
  background: linear-gradient(90deg,#00f7d2,#5ce9ff);
  color: #001018;
  padding: 8px 12px;
  border-radius: 10px;
  border: none;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
`;

export const GhostButton = styled.button`
  background: transparent;
  border: 1px solid #1f2937;
  color: #e5e7eb;
  padding: 8px 12px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
`;

export const BackButton = styled.button`
  font-size: 11px;
  border-radius: 999px;
  border: 1px solid #1f2937;
  padding: 4px 8px;
  background: #020617;
  color: #e5e7eb;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover {
    background: rgba(15, 23, 42, 0.6);
    border-color: rgba(0, 247, 210, 0.3);
  }
`;

export const NetworkBadge = styled.span`
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid #22c55e;
  background: rgba(34,197,94,0.12);
  font-size: 11px;
  color: #bbf7d0;
`;

/* ========== MODAL NOTIFICATION ========== */
export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15,23,42,0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
`;

export const ModalCard = styled.div`
  background: #020617;
  border-radius: 16px;
  border: 1px solid #1f2937;
  padding: 16px 16px 12px;
  max-width: 360px;
  width: 100%;
  box-shadow: 0 22px 40px rgba(0,0,0,0.6);
`;

export const ModalTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 4px;
`;

export const ModalMessage = styled.div`
  font-size: 13px;
  color: #cbd5f5;
`;

export const ModalActions = styled.div`
  margin-top: 12px;
  display: flex;
  justify-content: flex-end;
`;

export const ModalButton = styled.button`
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid #1f2937;
  background: #0f172a;
  color: #e5e7eb;
  font-size: 12px;
  cursor: pointer;
`;

/* ========== KYC BANNER ========== */
export const BannerWrap = styled.div`
  border-radius: 12px;
  padding: 10px 12px;
  margin-bottom: 30px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  border: 1px solid
    ${({ level }) =>
      level === "info"
        ? "rgba(59,130,246,0.7)"
        : level === "warning"
        ? "rgba(234,179,8,0.9)"
        : "rgba(248,113,113,0.95)"};
  background: ${({ level }) =>
    level === "info"
      ? "rgba(59,130,246,0.13)"
      : level === "warning"
      ? "rgba(234,179,8,0.14)"
      : "rgba(248,113,113,0.15)"};
`;

export const BannerIcon = styled.div`
  font-size: 16px;
  line-height: 1;
  margin-top: 1px;
`;

export const BannerBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const BannerTitle = styled.div`
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ level }) =>
    level === "info" ? "#60a5fa" : level === "warning" ? "#facc15" : "#fb7185"};
`;

export const BannerText = styled.div`
  font-size: 13px;
`;

export const BannerFooter = styled.div`
  margin-top: 4px;
`;

export const BannerLinkButton = styled.button`
  margin-top: 2px;
  font-size: 12px;
  border-radius: 999px;
  border: 1px solid rgba(148,163,184,0.7);
  background: transparent;
  color: #e5e7eb;
  padding: 4px 10px;
  cursor: pointer;
`;

/* ========== TREEMAP ========== */
export const treemapPulse = keyframes`
  0% { transform: scale(1); box-shadow: 0 0 0 rgba(0,247,210,0); }
  50% { transform: scale(1.01); box-shadow: 0 0 20px rgba(0,247,210,0.35); }
  100% { transform: scale(1); box-shadow: 0 0 0 rgba(0,247,210,0); }
`;

export const TreemapBar = styled.div`
  display: flex;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #1f2937;
  background: #020617;
  height: 70px;
  animation: ${treemapPulse} 5s ease-in-out infinite;
`;

export const TreemapSegment = styled.div`
  position: relative;
  height: 100%;
  min-width: ${({ width }) => (width < 8 ? 8 : width)}%;
  background: linear-gradient(135deg,#00f7d2,#5ce9ff);
  border-right: 1px solid rgba(15,23,42,0.9);
`;

export const TreemapLabel = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  color: #001018;
  text-shadow: 0 0 4px rgba(255,255,255,0.4);
`;

/* ========== SIDEBAR COMPONENTS ========== */
const slideIn = keyframes`
  from { transform: translateX(-380px); opacity: 0; }
  to   { transform: translateX(0); opacity: 1; }
`;

export const SidebarOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  backdrop-filter: blur(4px);
  z-index: 9998;
`;

export const SidebarGlass = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 380px;
  height: 100vh;
  background: linear-gradient(135deg, rgba(15,23,42,0.65), rgba(0,247,210,0.08));
  backdrop-filter: blur(22px) brightness(1.15);
  border-right: 1px solid rgba(0,247,210,0.35);
  box-shadow: 0 0 35px rgba(0,247,210,0.25);
  padding: 22px;
  animation: ${slideIn} 0.35s ease forwards;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  @media (max-width: 640px) {
    width: 100%;
  }
`;

/* ========== ACTIVITY VIEW COMPONENTS ========== */
export const ActivityContainer = styled.div`
  margin-top: 16px;
`;

export const ActivityHeader = styled(FlexRow)`
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
`;

export const ActivityTitle = styled.h4`
  font-size: 13px;
  color: #94a3b8;
  margin: 0;
  font-weight: 600;
`;

export const HashContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const HashLink = styled.span`
  font-family: monospace;
  font-size: 11px;
  color: #5ce9ff;
  cursor: pointer;
  text-decoration: underline;
  text-decoration-color: rgba(92, 233, 255, 0.5);
  transition: all 0.2s ease;

  &:hover {
    color: #00f7d2;
    text-decoration-color: rgba(0, 247, 210, 0.8);
  }
`;

export const CopyButton = styled.button`
  font-size: 10px;
  border-radius: 4px;
  border: 1px solid #1f2937;
  padding: 2px 6px;
  background: #020617;
  color: #5ce9ff;
  cursor: pointer;
  transition: all 0.2s ease;
  line-height: 1.2;

  &:hover {
    background: rgba(92, 233, 255, 0.1);
    border-color: #5ce9ff;
  }
`;

export const HashText = styled.span`
  font-family: monospace;
  font-size: 11px;
`;

export const DescText = styled.span`
  font-family: monospace;
  font-size: 11px;
`;

export const EmptyStateTd = styled(SmallTd)`
  text-align: center;
  padding: 20px;
  color: #94a3b8;
`;

